import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { breadcrumbLd, faqLd, ogUrl } from "@/components/site/Seo";
import { API } from "@/lib/api";
import { useCurrency, fmt } from "@/lib/currency";
import { track } from "@/lib/analytics";

const BASE = "https://thesourcingatlas.com";
const ITINERARY = [
  ["Day 1", "Arrival, airport pickup, hotel check-in near the fair, evening orientation and briefing."],
  ["Day 2", "Fair-day one: badge collection, hall walk-through, first booth shortlist review."],
  ["Day 3", "Fair-day two: targeted booth visits with interpreter, sample collection, quote logging."],
  ["Day 4", "Market or factory-belt day: guided visit and negotiation support."],
  ["Day 5", "Evening quote-review session, consolidation planning, departure."],
];
const TOUR_FAQ = [
  { q: "How much does a Canton Fair tour cost?", a: "Each cohort is priced publicly in both INR and USD on this page and set per departure. Silver, Gold and Platinum tiers differ by interpreter access, booth shortlisting and follow-through." },
  { q: "What is included in a tour?", a: "Visa invitation and badge pre-registration, a hotel block near the fair, airport pickup, fair-day briefings, and WhatsApp support — with interpreters and curated booth shortlists on higher tiers." },
  { q: "Do I need experience to join?", a: "No. Most travellers are first-time importers; briefings, interpreters and curated shortlists are built for beginners." },
  { q: "What is not included?", a: "Airfare and visa fees are excluded unless stated. Deposits and balances are shown at checkout with the refund policy linked before payment." },
];

export default function Tours() {
  const { currency } = useCurrency();
  const [cohorts, setCohorts] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    fetch(`${API}/cohorts`).then((r) => r.json()).then(setCohorts).catch(() => {});
  }, []);

  return (
    <SiteLayout>
      <Seo
        title="Canton Fair Tour Package India — Small-Cohort Buyer Tours | The Sourcing Atlas"
        description="Canton Fair and Yiwu market tours in small cohorts with interpreters, escorted factory visits, visa and hotel support. Silver, Gold and Platinum tiers, priced in INR and USD."
        path="/tours"
        ogImage={ogUrl("Canton Fair & Market Tours", "Upcoming Batches")}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Tours", path: "/tours" }]), faqLd(TOUR_FAQ)]}
      />
      <div className="crumb"><Link to="/">Home</Link> › Tours</div>
      <div className="pband">
        <div className="pband-in">
          <span className="tierpill tn">UPCOMING BATCHES</span>
          <h1>Canton Fair &amp; Market Tours</h1>
          <p className="lede">Small cohorts. Real interpreters. Escorted factory visits. Everything handled from your city to Guangzhou and back.</p>
        </div>
      </div>

      <section>
        <div className="wrap">
          <div className="tours">
            {cohorts.map((t) => {
              const productLd = null;
              const soldOut = (t.seats_left || 0) <= 0;
              return (
                <div className={"tour" + (t.featured ? " feat" : "")} key={t.id} data-testid={`cohort-${t.id}`}>
                  <div className="hd">
                    <div className="d">{t.start_date} → {t.end_date} · {t.tour}</div>
                    <h3>{t.name}</h3>
                  </div>
                  <div className="bd">
                    <span className="seats">{soldOut ? "Sold out" : `${t.seats_left} of ${t.seats_total} seats left`}</span>
                    <ul>{(t.inclusions || []).map((i, k) => <li key={k}>{i}</li>)}</ul>
                    <div className="price" data-testid={`price-${t.id}`}>
                      {fmt(currency, t.price_inr, t.price_usd)} <small>/ person</small>
                    </div>
                    <div className="pnote">Excludes airfare &amp; visa fees · Deposit {t.deposit_pct || 30}% option at checkout</div>
                    {soldOut ? (
                      <Link className="btn btn-line" to="/contact" onClick={() => track("cta_click", { cta: "waitlist" })}>Join the Waitlist</Link>
                    ) : (
                      <Link className="btn btn-red" to={`/checkout/tour/${t.id}`} onClick={() => track("checkout_started", { cohort: t.id })} data-testid={`reserve-${t.id}`}>Reserve a Seat</Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {cohorts.length === 0 && <p style={{ textAlign: "center", color: "var(--grey)" }}>Loading cohorts…</p>}
        </div>
      </section>

      <section style={{ background: "var(--wash)" }}>
        <div className="wrap" style={{ maxWidth: 860 }}>
          <div className="k2">DAY BY DAY</div>
          <h2>A Typical Cohort Itinerary</h2>
          <div style={{ marginTop: 28 }}>
            {ITINERARY.map(([d, p], i) => (
              <details key={i} open={open === i} onClick={() => setOpen(i)}>
                <summary>{d}</summary>
                <p>{p}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section>
        <div className="wrap" style={{ maxWidth: 860 }}>
          <div className="k2">FAQ</div>
          <h2>Tour Questions</h2>
          <div style={{ marginTop: 28 }}>
            {TOUR_FAQ.map((f, i) => (
              <details key={i} open={i === 0}><summary>{f.q}</summary><p>{f.a}</p></details>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
